/**
 * Button Constants
 * Centralized configuration values for Material 3 button specifications
 */

import type { ButtonSize, SizeConfig } from './types';

// ============================================================================
// ANIMATION TOKENS
// ============================================================================

export const ANIMATION_TOKENS = {
  duration: {
    long: 300,
    medium: 200,
    short: 150,
  },
  easing: {
    accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
  },
} as const;

// ============================================================================
// CSS CLASS NAMES
// ============================================================================

export const CSS_CLASSES = {
  endIcon: 'Button-endIcon',
  label: 'Button-label',
  morphing: 'Button-morphing',
  root: 'Button-root',
  selected: 'Button-selected',
  startIcon: 'Button-startIcon',
} as const;

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULTS = {
  morphShape: false,
  shape: 'round',
  size: 'sm',
  smallPadding: 'recommended',
  toggle: false,
  variant: 'filled',
} as const;

// ============================================================================
// ELEVATION TOKENS
// ============================================================================

export const ELEVATION_TOKENS = {
  elevated: {
    default: 1,
    disabled: 0,
    hover: 2,
  },
  filled: {
    default: 0,
    disabled: 0,
    hover: 0,
  },
} as const;

// ============================================================================
// SIZE CONFIGURATIONS
// ============================================================================

/**
 * Size configuration lookup table
 * Based on Material 3 Expressive specifications
 *
 * | Size | Height | Padding | Font | Icon | Gap  | Radius (round) | Radius (square) |
 * |------|--------|---------|------|------|------|----------------|-----------------|
 * | lg   | 96dp   | 48dp    | 20px | 28px | 12px | 48px           | 24px            |
 * | md   | 56dp   | 24dp    | 16px | 20px | 8px  | 28px           | 16px            |
 * | sm   | 40dp   | 16dp*   | 14px | 18px | 8px  | 20px           | 12px            |
 * | xl   | 136dp  | 64dp    | 24px | 36px | 16px | 68px           | 32px            |
 * | xs   | 32dp   | 12dp    | 12px | 16px | 4px  | 16px           | 8px             |
 *
 * *Small size has optional 24dp deprecated padding
 */
export const SIZE_CONFIG: Record<ButtonSize, SizeConfig> = {
  lg: {
    borderRadius: 48,
    fontSize: 20,
    height: 96,
    iconGap: 12,
    iconSize: 28,
    minWidth: 120,
    paddingX: 48,
    squareBorderRadius: 24,
  },
  md: {
    borderRadius: 28,
    fontSize: 16,
    height: 56,
    iconGap: 8,
    iconSize: 20,
    minWidth: 80,
    paddingX: 24,
    squareBorderRadius: 16,
  },
  sm: {
    borderRadius: 20,
    fontSize: 14,
    height: 40,
    iconGap: 8,
    iconSize: 18,
    minWidth: 64,
    paddingX: 16,
    paddingXDeprecated: 24,
    squareBorderRadius: 12,
  },
  xl: {
    borderRadius: 68,
    fontSize: 24,
    height: 136,
    iconGap: 16,
    iconSize: 36,
    minWidth: 160,
    paddingX: 64,
    squareBorderRadius: 32,
  },
  xs: {
    borderRadius: 16,
    fontSize: 12,
    height: 32,
    iconGap: 4,
    iconSize: 16,
    minWidth: 48,
    paddingX: 12,
    squareBorderRadius: 8,
  },
} as const;

// ============================================================================
// STATE LAYER OPACITY
// ============================================================================

export const STATE_LAYER_OPACITY = {
  dragged: 0.16,
  focus: 0.12,
  hover: 0.08,
  pressed: 0.12,
} as const;
